import { configs } from '../configs';

export interface Shift {
  id: string;
  title: string;
  description?: string;
  startTime: Date | string;
  endTime: Date | string;
  staffId: string;
  staffName: string;
  position?: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  color?: string;
  isRecurring: boolean;
  recurringPattern?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    daysOfWeek?: number[];
    endDate?: Date;
  };
  bonusAmount?: number;
  bonusCurrency?: string;
  createdBy: string;
  tenantId: string;
  createdAt?: string;
  updatedAt?: string;
}

class ShiftService {
  private async getAuthHeader(): Promise<Record<string, string>> {
    try {
      const tokenRaw =
        localStorage.getItem('auth.accessToken') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('token');

      if (tokenRaw) {
        let token = tokenRaw;
        try {
          const parsed = JSON.parse(tokenRaw);
          if (parsed && typeof parsed === 'object' && parsed.value) {
            token = parsed.value;
          }
        } catch (e) {
          token = tokenRaw;
        }

        return {
          Authorization: `Bearer ${token}`,
        };
      }

      const sessionTokenRaw =
        sessionStorage.getItem('auth.accessToken') ||
        sessionStorage.getItem('authToken') ||
        sessionStorage.getItem('token');
      if (sessionTokenRaw) {
        let sessionToken = sessionTokenRaw;
        try {
          const parsed = JSON.parse(sessionTokenRaw);
          if (parsed && typeof parsed === 'object' && parsed.value) {
            sessionToken = parsed.value;
          }
        } catch (e) {
          sessionToken = sessionTokenRaw;
        }

        return {
          Authorization: `Bearer ${sessionToken}`,
        };
      }

      return {};
    } catch (error) {
      console.error('Error getting auth header:', error);
      return {};
    }
  }

  async getShifts(): Promise<Shift[]> {
    try {
      const authHeaders = await this.getAuthHeader();
      // Request a large number of shifts (size=0 means no limit in the API)
      // Using size=1000 to get all shifts, or we can paginate if needed
      const queryParams = new URLSearchParams();
      queryParams.append('page', '1');
      queryParams.append('size', '1000'); // Request up to 1000 shifts
      
      const response = await fetch(`${configs.apiRoot}/shifts?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch shifts: ${response.status}`);
      }

      const data = await response.json();
      // Handle both array response and paginated response
      if (Array.isArray(data)) {
        return data;
      } else if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching shifts:', error);
      throw error;
    }
  }

  async getShiftsByStaffId(staffId: string): Promise<Shift[]> {
    try {
      const allShifts = await this.getShifts();
      return allShifts.filter((shift) => shift.staffId === staffId);
    } catch (error) {
      console.error(`Error fetching shifts for staff ${staffId}:`, error);
      throw error;
    }
  }

  async getShiftsByDateRange(
    startDate: Date,
    endDate: Date
  ): Promise<Shift[]> {
    try {
      const authHeaders = await this.getAuthHeader();
      
      // Format dates for API filter (ISO strings)
      const startDateStr = startDate.toISOString();
      const endDateStr = endDate.toISOString();
      
      // Build query with date range filter
      const queryParams = new URLSearchParams();
      queryParams.append('page', '1');
      queryParams.append('size', '1000'); // Large enough for any reasonable date range
      
      // Create filter for date range - shifts that start within the range
      const filter = {
        startTime: {
          $gte: startDateStr,
          $lte: endDateStr,
        },
      };
      queryParams.append('filter', JSON.stringify(filter));
      
      const response = await fetch(`${configs.apiRoot}/shifts?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch shifts: ${response.status}`);
      }

      const data = await response.json();
      // Handle both array response and paginated response
      if (Array.isArray(data)) {
        return data;
      } else if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching shifts by date range:', error);
      throw error;
    }
  }

  async getShiftById(id: string): Promise<Shift | null> {
    try {
      const shifts = await this.getShifts();
      return shifts.find((shift) => shift.id === id) || null;
    } catch (error) {
      console.error(`Error fetching shift ${id}:`, error);
      throw error;
    }
  }

  async createShift(shift: Partial<Shift>): Promise<Shift> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/shifts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(shift),
      });

      if (!response.ok) {
        throw new Error(`Failed to create shift: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating shift:', error);
      throw error;
    }
  }

  async updateShift(id: string, shift: Partial<Shift>): Promise<Shift> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/shifts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(shift),
      });

      if (!response.ok) {
        throw new Error(`Failed to update shift: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating shift ${id}:`, error);
      throw error;
    }
  }

  async deleteShift(id: string): Promise<void> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/shifts/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete shift: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error deleting shift ${id}:`, error);
      throw error;
    }
  }
}

export const shiftService = new ShiftService();

